/**
 * NOS-158: presentational theme gallery grid, shared by onboarding (light)
 * and the builder overlay (dark). Cards show the preview screenshot when the
 * package has one, otherwise a generated swatch from the package tokens.
 */
import type { GalleryTheme } from "@/lib/themeCatalog"

export interface ThemeGalleryProps {
  themes: GalleryTheme[]
  selectedId: string | null
  onSelect: (theme: GalleryTheme) => void
  mode: "light" | "dark"
}

function swatchColors(theme: GalleryTheme): { primary: string; secondary: string; font: string } {
  const tokens = theme.package.tokens
  return {
    primary: tokens?.colors?.primary ?? "#3b82f6",
    secondary: tokens?.colors?.secondary ?? "#1e40af",
    font: tokens?.typography?.headingFamily ?? "Inter, sans-serif",
  }
}

export function ThemeGallery({ themes, selectedId, onSelect, mode }: ThemeGalleryProps) {
  const dark = mode === "dark"
  const text = dark ? "#fff" : "#0f172a"
  const textMuted = dark ? "rgba(255,255,255,0.45)" : "#64748b"
  const cardBg = dark ? "rgba(255,255,255,0.03)" : "#fff"
  const border = dark ? "rgba(255,255,255,0.08)" : "#e2e8f0"

  return (
    <div
      data-testid="theme-gallery"
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
        gap: 12,
      }}
    >
      {themes.map((theme) => {
        const selected = theme.id === selectedId
        const colors = swatchColors(theme)
        return (
          <button
            key={theme.id}
            type="button"
            data-testid={`theme-card-${theme.id}`}
            onClick={() => onSelect(theme)}
            aria-pressed={selected}
            style={{
              textAlign: "left",
              padding: 0,
              borderRadius: 10,
              overflow: "hidden",
              cursor: "pointer",
              background: cardBg,
              border: selected ? `2px solid ${colors.primary}` : `1px solid ${border}`,
              outlineOffset: 2,
              boxShadow: selected ? `0 0 0 3px ${colors.primary}33` : "none",
              transition: "box-shadow 0.12s, border-color 0.12s",
            }}
          >
            {theme.preview_image ? (
              <img
                src={theme.preview_image}
                alt={`${theme.name} theme preview`}
                loading="lazy"
                style={{ width: "100%", aspectRatio: "4 / 3", objectFit: "cover", objectPosition: "top", display: "block" }}
              />
            ) : (
              <div
                aria-hidden="true"
                style={{
                  width: "100%",
                  aspectRatio: "4 / 3",
                  background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <span
                  style={{
                    fontFamily: colors.font,
                    fontSize: 26,
                    fontWeight: 700,
                    color: "#fff",
                    textShadow: "0 1px 3px rgba(0,0,0,0.25)",
                  }}
                >
                  Aa
                </span>
              </div>
            )}
            <div style={{ padding: "8px 10px 10px" }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: text }}>{theme.name}</div>
              {theme.cause_areas.length > 0 && (
                <div style={{ marginTop: 3, display: "flex", gap: 4, flexWrap: "wrap" }}>
                  {theme.cause_areas.slice(0, 3).map((cause) => (
                    <span
                      key={cause}
                      style={{
                        fontSize: 9,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        color: textMuted,
                        border: `1px solid ${border}`,
                        borderRadius: 999,
                        padding: "1px 6px",
                      }}
                    >
                      {cause}
                    </span>
                  ))}
                </div>
              )}
              {theme.description && (
                <div
                  style={{
                    marginTop: 3,
                    fontSize: 10.5,
                    lineHeight: 1.4,
                    color: textMuted,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {theme.description}
                </div>
              )}
            </div>
          </button>
        )
      })}
    </div>
  )
}
